import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  ClientData,
  ConfigurationExchangeInfo,
  LanguageNameSpaces,
} from "@shared/types";
import { EXCHANGES, MAX_ACCOUNT_NAME_LENGTH } from "@shared/common/constants";
import { getAccountFullName } from "@shared/features/portfolio/utils";
import { QuickConnectContext } from "@shared/features/onboarding/QuickConnect.context";
import { getAccountNameError } from "@shared/features/configuration/utils";
import { useClientsData } from "@shared/utils/hooks/misc";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface UseSelectSubExchangeProps {
  primaryExchange?: string;
  account?: ClientData;
  isEdit?: boolean;
}

const useSelectSubExchange = ({
  primaryExchange,
  account,
  isEdit,
}: UseSelectSubExchangeProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { isExternalConnectAccount, inviteData } =
    useContext(ExternalConnectAccountContext) || {};
  const { noFuturesAccountMessageData, quickConnectSucceeded } =
    useContext(QuickConnectContext) || {};
  const {
    aggExchanges,
    selectedSubExchanges,
    setSelectedSubExchanges,
    isDemoWorkspace,
    accountName,
    setAccountName,
    setAccountNameError,
    addClientSucceeded,
  } = useContext(AccountFormContext)!;
  const { clientsData: accountsList } = useClientsData() || {};
  const hasPresetName =
    !!isExternalConnectAccount && !!inviteData?.account_prefix;

  const [accountNameInitialized, setAccountNameInitialized] = useState(false);

  const subExchanges = useMemo(
    () => (primaryExchange ? aggExchanges[primaryExchange] : []),
    [aggExchanges, primaryExchange]
  );

  useEffect(() => {
    if (!selectedSubExchanges && subExchanges?.length) {
      setSelectedSubExchanges(subExchanges);
    }
  }, [subExchanges, selectedSubExchanges]);

  const getAccountsNames = useCallback(
    (name: string) => {
      return selectedSubExchanges?.map((subExchange) =>
        getAccountFullName(name, subExchange).toLowerCase()
      );
    },
    [selectedSubExchanges]
  );

  const getDefaultNameByExchange = useCallback(() => {
    const rawAccountNames =
      accountsList?.map((account) => account.client_name) ??
      inviteData?.existing_client_names;
    const accountNames = rawAccountNames?.map((name) => name.toLowerCase());
    let index: number | undefined;
    let defaultName = `My ${primaryExchange}`;
    if (isDemoWorkspace) defaultName = defaultName + " Demo";
    let defaultAccountsNames = getAccountsNames(defaultName);

    while (
      accountNames?.some((accountName) =>
        defaultAccountsNames?.includes(accountName)
      )
    ) {
      if (!index) {
        index = 2;
        defaultName = defaultName + ` ${index}`;
        defaultAccountsNames = getAccountsNames(defaultName);
      } else {
        index += 1;
        defaultName = defaultName.replace(/\d*$/, `${index}`);
        defaultAccountsNames = getAccountsNames(defaultName);
      }
    }
    return defaultName;
  }, [
    selectedSubExchanges,
    isDemoWorkspace,
    primaryExchange,
    accountsList,
    getAccountsNames,
    inviteData,
  ]);

  useEffect(() => {
    if (accountName?.length && !accountNameInitialized) {
      setAccountNameInitialized(true);
    }
  }, [accountName, accountNameInitialized]);

  useEffect(() => {
    if (account) {
      setAccountName(account.client_name);
    }
  }, [account]);

  useEffect(() => {
    if (
      selectedSubExchanges &&
      accountName === undefined &&
      !accountNameInitialized &&
      !isEdit &&
      primaryExchange &&
      (accountsList || !hasPresetName)
    ) {
      const defaultName = getDefaultNameByExchange();
      setAccountName(defaultName);
    }
  }, [
    accountName,
    isEdit,
    primaryExchange,
    getDefaultNameByExchange,
    accountsList,
    isDemoWorkspace,
    accountNameInitialized,
    selectedSubExchanges,
  ]);

  const checkNameAlreadyUsed = useCallback(
    (name: string) => {
      const accountsNames = getAccountsNames(name);
      return accountsList
        ?.filter(
          (accountInfo) =>
            !account || account.client_id !== accountInfo.client_id
        )
        .some((accountInfo) =>
          isEdit
            ? accountInfo.client_name.toLowerCase() === name.toLowerCase()
            : accountsNames?.includes(accountInfo.client_name.toLowerCase())
        );
    },
    [accountsList, account, isEdit, getAccountsNames]
  );

  const isLongName = (name: string) => {
    return name.length > MAX_ACCOUNT_NAME_LENGTH;
  };

  const checkNameIsTooLong = useCallback(
    (name: string) => {
      if (isEdit) {
        return isLongName(name);
      }
      const accountsNames = getAccountsNames(name);
      return accountsNames?.some((accountName) => isLongName(accountName));
    },
    [isEdit, getAccountsNames]
  );

  const getIsChecked = (subExchange: ConfigurationExchangeInfo) =>
    !!selectedSubExchanges?.find(
      (selectedExchange) => selectedExchange.exchange === subExchange.exchange
    );

  const onCheckboxChange = (subExchange: ConfigurationExchangeInfo) => {
    const isChecked = getIsChecked(subExchange);
    let updatedExchanges: ConfigurationExchangeInfo[];
    if (isChecked) {
      updatedExchanges =
        selectedSubExchanges?.filter(
          (selectedExchange) =>
            selectedExchange.exchange !== subExchange.exchange
        ) || [];
    } else {
      updatedExchanges = [...(selectedSubExchanges || []), subExchange];
    }
    setSelectedSubExchanges(updatedExchanges);
  };

  const getExchangeError = (subExchange: ConfigurationExchangeInfo) => {
    return (
      (
        [EXCHANGES.BINANCE_FUTURES, EXCHANGES.BINANCE_COIN_FUTURES] as string[]
      ).includes(subExchange.exchange) && noFuturesAccountMessageData?.show
    );
  };

  const isNameAlreadyUsed = (name: string) => {
    return accountsList?.some(
      (accountInfo) =>
        accountInfo.client_name.toLowerCase() === name.toLowerCase()
    );
  };

  const getCheckboxData = (subExchange: ConfigurationExchangeInfo) => {
    const accountDisplayName =
      accountName && !hasPresetName
        ? getAccountFullName(accountName, subExchange)
        : undefined;
    const checked = getIsChecked(subExchange);
    return {
      key: subExchange.exchange,
      value: subExchange.exchange,
      checked,
      onChange: () => onCheckboxChange(subExchange),
      exchange: subExchange.exchange_display,
      exchangeError: checked && getExchangeError(subExchange),
      account: accountDisplayName,
      accountError:
        checked &&
        !addClientSucceeded &&
        !quickConnectSucceeded &&
        accountDisplayName &&
        (isLongName(accountDisplayName) ||
          isNameAlreadyUsed(accountDisplayName)),
      description: subExchange.description,
    };
  };

  const accountNameError = useMemo(() => {
    if (
      !accountNameInitialized ||
      addClientSucceeded ||
      quickConnectSucceeded ||
      hasPresetName
    )
      return;
    if (!accountName?.length) return t("create_account.account_name_required");
    else if (checkNameIsTooLong(accountName)) {
      return getAccountNameError();
    } else if (checkNameAlreadyUsed(accountName)) {
      return t("create_account.account_name_used");
    }
  }, [
    accountName,
    checkNameIsTooLong,
    checkNameAlreadyUsed,
    accountNameInitialized,
    addClientSucceeded,
    quickConnectSucceeded,
    hasPresetName,
  ]);

  useEffect(() => {
    setAccountNameError(!!accountNameError);
  }, [accountNameError]);

  const accountNameInputData = {
    label: t("create_account.account_name"),
    value: accountName,
    onChange: (value: string) => setAccountName(value),
    error: accountNameError,
    shouldDisplay: !hasPresetName,
  };

  return {
    subExchanges,
    getCheckboxData,
    accountNameInputData,
    accountNameInitialized,
  };
};

export default useSelectSubExchange;
